import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import MainHeader from './MainHeader'
import HomeFooter from './HomeFooter'
import { CONTACT_INFO, SITE_LINKS } from '../constants/siteLinks'
import useTemplateScripts from '../hooks/useTemplateScripts'
import {
  clearPendingPayment,
  initiatePayment,
  paymentErrorMessage,
  readPendingPayment,
  rememberPendingPayment,
  waitForPayment,
} from '../services/paymentService'
import './Checkout.css'

const RETRY_OPTIONS = [
  { id: 'CARD', kind: 'card', label: 'Card', icon: 'fa-credit-card' },
  { id: 'MTN', kind: 'momo', label: 'MTN MoMo', logo: '/assets/img/brands/MTN-logo.jpg' },
  { id: 'AIRTEL', kind: 'momo', label: 'Airtel Money', logo: '/assets/img/brands/Airtel-logo.png', logoContain: true },
]

const formatAmount = (amount, currency) =>
  `${currency} ${Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: currency === 'RWF' ? 0 : 2,
    maximumFractionDigits: currency === 'RWF' ? 0 : 2,
  })}`

/**
 * Landing page after an online payment (the card page redirects here with ?paymentId=...).
 * Polls the payment until it is paid or failed, and lets the customer retry a failed payment.
 */
const PaymentReturn = () => {
  useTemplateScripts()
  const [searchParams] = useSearchParams()
  const [paymentId, setPaymentId] = useState(searchParams.get('paymentId') || readPendingPayment()?.paymentId || null)
  // phase: 'checking' | 'paid' | 'failed' | 'pending' | 'missing'
  const [phase, setPhase] = useState(paymentId ? 'checking' : 'missing')
  const [payment, setPayment] = useState(null)
  const [order, setOrder] = useState(null)
  const [error, setError] = useState('')
  const [retryMethod, setRetryMethod] = useState('CARD')
  const [retryPhone, setRetryPhone] = useState('')
  const [retrying, setRetrying] = useState(false)
  const pollRef = useRef(null)

  const check = useCallback(async (id) => {
    pollRef.current?.abort()
    const controller = new AbortController()
    pollRef.current = controller
    setPhase('checking')
    setError('')
    try {
      const result = await waitForPayment(id, { intervalMs: 3000, timeoutMs: 2 * 60 * 1000, signal: controller.signal })
      if (controller.signal.aborted) return
      setPayment(result?.payment || null)
      setOrder(result?.order || null)
      const status = result?.payment?.status
      if (status === 'paid' || result?.order?.paymentStatus === 'paid') {
        clearPendingPayment()
        setPhase('paid')
      } else if (status === 'failed') {
        // Offer the same method again by default
        if (RETRY_OPTIONS.some((o) => o.id === result.payment.method)) setRetryMethod(result.payment.method)
        setRetryPhone((prev) => prev || result.payment.phone || '')
        setPhase('failed')
      } else {
        setPhase('pending')
      }
    } catch (err) {
      if (controller.signal.aborted) return
      setError(err.status === 404 ? 'We could not find this payment.' : err.message)
      setPhase('missing')
    }
  }, [])

  useEffect(() => {
    if (paymentId) check(paymentId)
    return () => pollRef.current?.abort()
  }, [paymentId, check])

  const retryOption = RETRY_OPTIONS.find((o) => o.id === retryMethod) || RETRY_OPTIONS[0]

  const retry = async () => {
    const orderId = order?.id || payment?.orderId || readPendingPayment()?.orderId
    if (!orderId) return
    if (retryOption.kind === 'momo' && retryPhone.replace(/\D/g, '').length < 9) {
      setError('Enter a valid mobile money number, e.g. 0788 123 456')
      return
    }
    setRetrying(true)
    setError('')
    try {
      const result = await initiatePayment(orderId, {
        method: retryMethod,
        phone: retryOption.kind === 'momo' ? retryPhone : payment?.phone,
      })
      if (result.nextAction === 'redirect' && result.redirectUrl) {
        rememberPendingPayment({ paymentId: result.payment.id, orderId, method: retryMethod })
        window.location.assign(result.redirectUrl)
        return
      }
      // Mobile money: follow the new attempt on this page
      setPaymentId(result.payment.id)
    } catch (err) {
      if (err.status === 409) {
        setPhase('paid')
        return
      }
      setError(paymentErrorMessage(err.message))
    } finally {
      setRetrying(false)
    }
  }

  const summary = payment ? (
    <dl className="pay-return__summary">
      <div>
        <dt>Order</dt>
        <dd>#{order?.id || payment.orderId}</dd>
      </div>
      <div>
        <dt>Amount</dt>
        <dd>{formatAmount(payment.amount, payment.currency)}</dd>
      </div>
      <div>
        <dt>Method</dt>
        <dd>{RETRY_OPTIONS.find((o) => o.id === payment.method)?.label || payment.method}</dd>
      </div>
    </dl>
  ) : null

  return (
    <div className="checkout-page">
      <MainHeader cartCount={0} />
      <main className="co-main">
        <div className="co-container">
          <section className="pay-return" aria-live="polite">
            {phase === 'checking' ? (
              <>
                <div className="checkout-paying__icon" aria-hidden="true">
                  <i className="fa-solid fa-spinner fa-spin" />
                </div>
                <h1>Confirming your payment…</h1>
                <p className="checkout-modal__lead">This usually takes a few seconds. Please keep this page open.</p>
              </>
            ) : null}

            {phase === 'paid' ? (
              <>
                <div className="checkout-success-icon" aria-hidden="true">
                  <i className="fa-solid fa-check" />
                </div>
                <h1>Payment received</h1>
                <p className="checkout-modal__lead">
                  Thank you! Your order is paid and confirmed. Our team will contact you shortly about pickup or delivery.
                </p>
                {summary}
                <div className="pay-return__actions">
                  <Link
                    to={order?.id || payment?.orderId ? `${SITE_LINKS.orders}?order=${order?.id || payment.orderId}` : SITE_LINKS.orders}
                    className="checkout-btn checkout-btn--primary"
                  >
                    <i className="fa-solid fa-box" aria-hidden="true" />
                    View my orders
                  </Link>
                  <Link to={SITE_LINKS.shop} className="checkout-btn checkout-btn--ghost">
                    Continue shopping
                  </Link>
                </div>
              </>
            ) : null}

            {phase === 'failed' ? (
              <>
                <div className="checkout-paying__icon checkout-paying__icon--error" aria-hidden="true">
                  <i className="fa-solid fa-xmark" />
                </div>
                <h1>Payment not completed</h1>
                <p className="checkout-modal__lead">
                  {payment?.errorMessage ? paymentErrorMessage(payment.errorMessage) : 'The payment was declined or cancelled.'}{' '}
                  Your order is saved.
                </p>
                {summary}

                <div className="pay-return__retry">
                  <div className="pay-method-list" role="radiogroup" aria-label="Payment method">
                    {RETRY_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={retryMethod === option.id}
                        className={`pay-method ${retryMethod === option.id ? 'is-active' : ''}`}
                        onClick={() => {
                          setRetryMethod(option.id)
                          setError('')
                        }}
                      >
                        <span className="pay-method__radio" aria-hidden="true" />
                        <span className={`pay-method__icon ${option.logoContain ? 'pay-method__icon--contain' : ''}`} aria-hidden="true">
                          {option.logo ? <img src={option.logo} alt="" /> : <i className={`fa-solid ${option.icon}`} />}
                        </span>
                        <span className="pay-method__body">
                          <strong>{option.label}</strong>
                        </span>
                      </button>
                    ))}
                  </div>

                  {retryOption.kind === 'momo' ? (
                    <label className="checkout-field">
                      <span>{retryOption.label} number</span>
                      <input
                        type="tel"
                        value={retryPhone}
                        onChange={(e) => setRetryPhone(e.target.value)}
                        placeholder="0788 123 456"
                        autoComplete="tel"
                      />
                    </label>
                  ) : null}
                </div>

                {error ? <p className="pay-unavailable">{error}</p> : null}

                <div className="pay-return__actions">
                  <button type="button" className="checkout-btn checkout-btn--primary" onClick={retry} disabled={retrying}>
                    {retrying ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                        Starting payment…
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-rotate-right" aria-hidden="true" />
                        Try again
                      </>
                    )}
                  </button>
                  <a href={CONTACT_INFO.phoneHref} className="checkout-btn checkout-btn--ghost">
                    <i className="fa-solid fa-headset" aria-hidden="true" />
                    Need help? Call {CONTACT_INFO.phone}
                  </a>
                </div>
              </>
            ) : null}

            {phase === 'pending' ? (
              <>
                <div className="checkout-paying__icon checkout-paying__icon--warn" aria-hidden="true">
                  <i className="fa-solid fa-hourglass-half" />
                </div>
                <h1>Payment still processing</h1>
                <p className="checkout-modal__lead">
                  We haven&apos;t received the final result yet. If you completed the payment, it will be confirmed shortly.
                </p>
                {summary}
                <div className="pay-return__actions">
                  <button type="button" className="checkout-btn checkout-btn--primary" onClick={() => check(paymentId)}>
                    <i className="fa-solid fa-rotate" aria-hidden="true" />
                    Check again
                  </button>
                  <Link to={SITE_LINKS.home} className="checkout-btn checkout-btn--ghost">
                    Back to home
                  </Link>
                </div>
              </>
            ) : null}

            {phase === 'missing' ? (
              <>
                <div className="checkout-paying__icon checkout-paying__icon--warn" aria-hidden="true">
                  <i className="fa-solid fa-circle-question" />
                </div>
                <h1>No payment to show</h1>
                <p className="checkout-modal__lead">
                  {error || 'We could not find a payment for this page.'} If you were charged, please contact us with your order number.
                </p>
                <div className="pay-return__actions">
                  <Link to={SITE_LINKS.home} className="checkout-btn checkout-btn--primary">
                    Back to home
                  </Link>
                  <a href={CONTACT_INFO.phoneHref} className="checkout-btn checkout-btn--ghost">
                    Call {CONTACT_INFO.phone}
                  </a>
                </div>
              </>
            ) : null}
          </section>
        </div>
      </main>
      <HomeFooter />
    </div>
  )
}

export default PaymentReturn
